import type { ApiKeyUsageDetail, ApiResponse, ModelUsageDetail, OpenAiUsagePage } from './types';
import { formatUtcDate } from './client';
import {
  createUnresolvedApiKeyUsage,
  ensureDailyEntry,
  type UsageAggregate,
} from './usage-aggregation';
import {
  OPENAI_GPT_LIVE_MODEL,
  isOpenAiGptLiveModel,
  parseOpenAiGptLiveCost,
} from './gpt-live';

const GPT_LIVE_UNATTRIBUTED_PREFIX = 'openai-unattributed-gpt-live';

export interface GptLiveCostAllocationResult {
  allocatedCost: number;
  ignoredRows: number;
}

function addGptLiveCostToApiKey(
  apiKeyUsage: ApiKeyUsageDetail,
  date: string,
  cost: number
): void {
  apiKeyUsage.cost += cost;
  if (apiKeyUsage.firstSeen === 'N/A' || date < apiKeyUsage.firstSeen) apiKeyUsage.firstSeen = date;
  if (apiKeyUsage.lastSeen === 'N/A' || date > apiKeyUsage.lastSeen) apiKeyUsage.lastSeen = date;

  const modelUsage: ApiKeyUsageDetail['modelUsage'][string] & { lastUsed?: string } =
    apiKeyUsage.modelUsage[OPENAI_GPT_LIVE_MODEL] ?? {
      model: OPENAI_GPT_LIVE_MODEL,
      contextTokens: 0,
      generatedTokens: 0,
      cachedTokens: 0,
      cost: 0,
      numberOfRequests: 0,
      provider: 'openai',
    };
  modelUsage.cost += cost;
  modelUsage.lastUsed = date;
  apiKeyUsage.modelUsage[OPENAI_GPT_LIVE_MODEL] = modelUsage;
  apiKeyUsage.modelCosts[OPENAI_GPT_LIVE_MODEL] =
    (apiKeyUsage.modelCosts[OPENAI_GPT_LIVE_MODEL] ?? 0) + cost;
  if (!apiKeyUsage.dailyModelCosts) apiKeyUsage.dailyModelCosts = {};
  if (!apiKeyUsage.dailyModelCosts[date]) apiKeyUsage.dailyModelCosts[date] = {};
  apiKeyUsage.dailyModelCosts[date][OPENAI_GPT_LIVE_MODEL] =
    (apiKeyUsage.dailyModelCosts[date][OPENAI_GPT_LIVE_MODEL] ?? 0) + cost;
}

/**
 * Reconcile GPT-Live 1's project-level, duration-based Costs API line into
 * model/day/key detail. The Costs API does not expose an API key for this line,
 * so the exact amount remains in an explicitly unattributed synthetic row.
 */
export function applyProjectGptLiveCosts(
  aggregate: UsageAggregate,
  costsUsage: ApiResponse<OpenAiUsagePage> | null
): GptLiveCostAllocationResult {
  if (!costsUsage?.success || !Array.isArray(costsUsage.data?.data)) {
    return { allocatedCost: 0, ignoredRows: 0 };
  }

  let allocatedCost = 0;
  let ignoredRows = 0;
  for (const bucket of costsUsage.data.data) {
    const startSeconds = Number(bucket?.start_time ?? bucket?.startTime);
    if (!Number.isFinite(startSeconds)) continue;
    const date = formatUtcDate(new Date(startSeconds * 1000));

    for (const result of Array.isArray(bucket?.results) ? bucket.results : []) {
      if (!isOpenAiGptLiveModel(result?.line_item ?? result?.lineItem)) continue;
      const cost = parseOpenAiGptLiveCost(result);
      if (cost === null || cost <= 0) {
        ignoredRows += 1;
        continue;
      }

      const projectId = result?.project_id ?? result?.projectId;
      const scope = typeof projectId === 'string' && projectId ? projectId : 'organization';
      const unresolvedKeyId = `${GPT_LIVE_UNATTRIBUTED_PREFIX}:${scope}`;
      let unresolved = aggregate.apiKeyUsageMap.get(unresolvedKeyId);
      if (!unresolved) {
        unresolved = createUnresolvedApiKeyUsage(unresolvedKeyId, date);
        unresolved.name = `Unattributed GPT-Live 1 cost (${scope})`;
        unresolved.projectId = scope === 'organization' ? undefined : scope;
        unresolved.projectIds = unresolved.projectId ? [unresolved.projectId] : undefined;
        unresolved.sourceIds = [unresolvedKeyId];
        aggregate.apiKeyUsageMap.set(unresolvedKeyId, unresolved);
      }
      addGptLiveCostToApiKey(unresolved, date, cost);

      const dailyData = ensureDailyEntry(aggregate, date);
      dailyData.cost += cost;
      dailyData.apiKeys.add(unresolvedKeyId);
      if (!dailyData.modelCosts) dailyData.modelCosts = {};
      dailyData.modelCosts[OPENAI_GPT_LIVE_MODEL] =
        (dailyData.modelCosts[OPENAI_GPT_LIVE_MODEL] ?? 0) + cost;
      if (!dailyData.breakdowns) dailyData.breakdowns = {};
      if (!dailyData.breakdowns.openai) {
        dailyData.breakdowns.openai = {
          provider: 'openai',
          requests: 0,
          cost: 0,
          contextTokens: 0,
          generatedTokens: 0,
          cachedTokens: 0,
          isEstimatedRequests: false,
        };
      }
      dailyData.breakdowns.openai.cost += cost;

      const modelUsage: ModelUsageDetail = aggregate.modelUsageMap.get(OPENAI_GPT_LIVE_MODEL) ?? {
        model: OPENAI_GPT_LIVE_MODEL,
        contextTokens: 0,
        generatedTokens: 0,
        cachedTokens: 0,
        cost: 0,
        numberOfRequests: 0,
        provider: 'openai',
      };
      modelUsage.cost += cost;
      if (!modelUsage.lastUsed || date > modelUsage.lastUsed) modelUsage.lastUsed = date;
      aggregate.modelUsageMap.set(OPENAI_GPT_LIVE_MODEL, modelUsage);

      aggregate.totals.cost += cost;
      allocatedCost += cost;
    }
  }

  return { allocatedCost, ignoredRows };
}
